import React from "react";
import { Link, useParams } from "react-router-dom";
import AdminPageShell from "./AdminPageShell";
import useFetch from "../../useFetch";

const formatDate = (date) => date ? new Date(date).toLocaleDateString() : "Not available";
const valueOrDash = (value) => value || "Not added";

const AdminCandidateDetails = () => {
  const { candidateId } = useParams();
  const { response, isLoading, error } = useFetch(process.env.REACT_APP_BASE_API_URL + `/admin/candidates/${candidateId}`);
  const candidate = response?.candidate;
  const summary = candidate?.summary ?? {};
  const profile = summary.candidate_profile ?? {};
  const stats = candidate?.stats ?? {};
  const applications = candidate?.applications ?? [];
  const savedJobs = candidate?.saved_jobs ?? [];
  const activity = candidate?.activity ?? [];

  const details = [
    { id: "epic-id", label: "Epic-Jobs ID", value: valueOrDash(summary.epic_jobs_id) },
    { id: "referral", label: "Referred By", value: summary.referred_by?.epic_jobs_id ? `${summary.referred_by.epic_jobs_id} (${summary.referred_by.name})` : "No referral" },
    { id: "name", label: "Name", value: valueOrDash(profile.full_name || summary.name) },
    { id: "email", label: "Email", value: valueOrDash(summary.email) },
    { id: "phone", label: "Phone", value: valueOrDash(profile.phone) },
    { id: "title", label: "Role", value: valueOrDash(profile.professional_title) },
    { id: "location", label: "Location", value: valueOrDash(profile.location) },
    { id: "experience", label: "Experience", value: valueOrDash(profile.experience) },
    { id: "salary", label: "Expected Salary", value: valueOrDash(profile.expected_salary) },
    { id: "joined", label: "Joined", value: formatDate(summary.created_at) },
    { id: "status", label: "Status", value: valueOrDash(summary.account_status) },
  ];

  const statCards = [
    { id: "applications", label: "Applications", value: stats.applications ?? 0 },
    { id: "saved", label: "Saved Jobs", value: stats.saved_jobs ?? 0 },
    { id: "shortlisted", label: "Shortlisted", value: stats.shortlisted ?? 0 },
    { id: "resume", label: "Resume Uploaded", value: stats.resume_uploaded ? "Yes" : "No" },
  ];

  return (
    <AdminPageShell
      active="candidates"
      title={`${summary.name ?? "Candidate"} Details`}
      text="Detailed candidate profile, applications, saved jobs, resume, and activity."
    >
      {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
      {isLoading && <h5 className="text-center my-3">Loading Data</h5>}

      {!error && !isLoading && (
        <div className="row">
          <div className="col-lg-7">
            <div className="ls-widget">
              <div className="widget-title">
                <h4>Candidate Details</h4>
                <Link to="/admin-candidates" className="theme-btn btn-style-three">Back</Link>
              </div>
              <div className="widget-content">
                <div className="admin-detail-grid">
                  {details.map((item) => (
                    <div className="admin-detail-item" key={item.id}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
                {profile.resume_url && (
                  <div className="admin-profile-resume-actions">
                    <a href={profile.resume_url} target="_blank" rel="noreferrer" className="theme-btn btn-style-one">
                      Open Resume
                    </a>
                    {profile.resume_download_url && (
                      <a href={profile.resume_download_url} target="_blank" rel="noreferrer" className="theme-btn btn-style-three">
                        Download
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="row">
              {statCards.map((item) => (
                <div className="col-md-6" key={item.id}>
                  <div className="ui-item">
                    <div className="right">
                      <h4>{item.value}</h4>
                      <p>{item.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="ls-widget">
              <div className="widget-title">
                <h4>Application History</h4>
              </div>
              <div className="widget-content">
                <div className="admin-table-list">
                  {applications.map((application) => (
                    <div className="admin-table-row" key={application.id}>
                      <div>
                        <strong>{application.job_title}</strong>
                        <span>{application.company_name} - {formatDate(application.created_at)}</span>
                      </div>
                      <span>{application.status}</span>
                    </div>
                  ))}
                  {applications.length === 0 && <div className="admin-empty-state">No applications found for this candidate.</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="ls-widget">
              <div className="widget-title">
                <h4>Activity Timeline</h4>
              </div>
              <div className="widget-content">
                <div className="admin-history-list">
                  {activity.map((item) => (
                    <div className="admin-history-item" key={item.id}>
                      <span>{formatDate(item.date)}</span>
                      <strong>{item.title}</strong>
                      <p>{item.detail}</p>
                    </div>
                  ))}
                  {activity.length === 0 && <div className="admin-empty-state">No activity found.</div>}
                </div>
              </div>
            </div>

            <div className="ls-widget">
              <div className="widget-title">
                <h4>Saved Jobs</h4>
              </div>
              <div className="widget-content">
                <div className="admin-table-list">
                  {savedJobs.map((job) => (
                    <div className="admin-table-row" key={job.id}>
                      <div>
                        <strong>{job.title}</strong>
                        <span>{job.company_name} - {job.location}</span>
                      </div>
                      <span>{job.status}</span>
                    </div>
                  ))}
                  {savedJobs.length === 0 && <div className="admin-empty-state">No saved jobs found.</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminPageShell>
  );
};

export default AdminCandidateDetails;
